import '../Account.css';
import React, { useState, useEffect, useRef, ReactNode } from 'react'
import { Form, Button, DropdownButton, Dropdown, ProgressBar, Modal } from "react-bootstrap";
import Switch from "react-switch";
import { useTranslation } from "react-i18next";
import useDroneHubConnection from '../../../hooks/DroneHubConnection';
import { DroneSetBaudRateRequestData, DroneUnbindModemRequestData, DroneUploadFirmwareRequestData, ModemData, SetBaudRateResponseSchema, UploadFirmwareResponseSchema } from '../../../api/Drone';
import { ModemSettingsData, ModemSettingsSaveSettingRequestData, ModemSettingsSchema } from '../../../api/ModemSettings';
import { AstraUserSubscriptionData, AstraUserSubscriptionSchema } from '../../../api/AstraSubscription';
import { APIRequestGet, APIRequestPost } from '../../../utils/APIRequests';
import { HubBaudRateMessage, HubBaudRateSchema, HubFirmwareProgressMessage, HubFirmwareProgressSchema } from '../../../asp/DroneHub';
import firmware from '../../../utils/firmware';

interface MainSettingsProps {
    drone: ModemData;
    isAstra: boolean;
}

const MainSettings: React.FC<MainSettingsProps> = ({ drone, isAstra }) => {
    const { t } = useTranslation(['modem']);

    const droneHub = useDroneHubConnection();

    const [showUnbindModel, setShowUnbindModel] = useState<boolean>(false);
    const [modemSettings, setModemSettings] = useState<ModemSettingsData | null>(null);
    const [rtspStr, setRtspStr] = useState<string>("");
    const [fpvModeSettings, setFpvModSettings] = useState<boolean>(false);
    const [fpvLatencySettings, setFpvLatencySettings] = useState<number>(400);
    const [saveSettingsInfo, setSaveSettingsInfo] = useState<ReactNode>(<></>);
    const [enabledSaveButton, setEnabledSaveButton] = useState<boolean>(false);

    const [firmwareLoad, setFirmwareLoad] = useState<ReactNode>(<></>);
    const [firmwareLoadState, setFirmwareLoadState] = useState<number>(0);
    const [firmwareLoadProgress, setFirmwareLoadProgress] = useState<ReactNode>(<></>);

    const [rate, setRate] = useState<string | null>(null);

    const [subscriptionInfo, setSubscriptionInfo] = useState<AstraUserSubscriptionData | null>();

    const mimei = useRef<string>("");

    useEffect(() => {
        droneHub.connection.on('BaudRate', (message: HubBaudRateMessage) => {
            const result = HubBaudRateSchema.safeParse(message);
            if (result.success) {
                console.log("MainSettings: BaudRate message received", result.data);
                if (mimei.current === result.data.imei) {
                    setRate(result.data.baudRate);
                }
            } else {
                console.error(result.error);
            }
        });

        droneHub.connection.on('FirmwareProgress', (message: HubFirmwareProgressMessage) => {
            const result = HubFirmwareProgressSchema.safeParse(message);
            if (result.success) {
                if (mimei.current === result.data.imei) {
                    setFirmwareLoadState(result.data.progress);
                    setFirmwareLoadProgress(<ProgressBar now={result.data.progress} label={`${result.data.progress}%`} />);
                }
            } else {
                console.error(result.error);
            }
        });
    }, [droneHub]);

    useEffect(() => {
        setRate(null);
        setModemSettings(null)
        setSaveSettingsInfo(<></>);
        mimei.current = drone.imei;
        getModemSettings(drone.imei);
        setFirmwareLoad(<></>);
        getSubscriptionInfo();
    }, [drone]);

    useEffect(() => {
        setSaveSettingsInfo(<></>);
        setEnabledSaveButton(true);
    }, [modemSettings, rtspStr, fpvModeSettings, fpvLatencySettings]);

    function unbind(): void {
        setShowUnbindModel(true);
    }

    function setBaudRate(value: number): void {
        const data: DroneSetBaudRateRequestData = {
            baudRate: value,
            imei: drone.imei
        };

        APIRequestPost('/drone/setBaudRate', {
            data: data,
            schema: SetBaudRateResponseSchema,
            logSuccess: true
        });
    }

    function saveModemSettings(): void {
        const data: ModemSettingsSaveSettingRequestData = {
            imei: drone.imei,
            rtspUrl: rtspStr,
            fpvMode: fpvModeSettings,
            fpvLatency: fpvLatencySettings.toString()
        };

        APIRequestPost('/modemSettings/saveSettings', {
            data: data,
            logSuccess: true
        }).then(() => {
            setSaveSettingsInfo(<label>Настройки сохранены</label>);
            setEnabledSaveButton(false);
        }).catch(() => {
            setSaveSettingsInfo(<label>Ошибка сохранения настроек</label>);
        });
    }

    function getModemSettings(imei: string): void {
        APIRequestGet(`/modemSettings/settings?imei=${imei}`, {
            schema: ModemSettingsSchema,
            logSuccess: true
        }).then((result) => {
            setModemSettings(result.data);
            setRtspStr(result.data.rtspUrl);
            setFpvModSettings(result.data.fpvMode);
            setFpvLatencySettings(result.data.fpvLatency);
        });
    }

    function confirmUnbind(): void {
        const data: DroneUnbindModemRequestData = {
            imei: drone.imei
        };

        APIRequestPost('/drone/unbindModem', {
            data: data,
            logSuccess: true
        }).then(() => {
            window.location.reload();
        });
    }

    function getSubscriptionInfo() {
        APIRequestGet(`/astraSubscription/getSubscription?modemId=${drone.id}`, {
            schema: AstraUserSubscriptionSchema,
            logSuccess: true
        }).then((result) => {
            setSubscriptionInfo(result.data);
        });
    }

    function loadLastFirmware(): void {
        const data: DroneUploadFirmwareRequestData = {
            imei: drone.imei
        };

        APIRequestPost('/drone/uploadfirmware', {
            data: data,
            schema: UploadFirmwareResponseSchema,
            logSuccess: true
        }).then((result) => {
            if (typeof result.data !== "string") {
                if (result.data.state === "Ok") {
                    if (drone.version < 2002) {
                        setFirmwareLoad(<p>Load firmware: {firmwareLoadState}%</p>);
                    } else {
                        setFirmwareLoad(<p>{t('firmware_in_process_updating')}</p>);
                    }
                }
                else if (result.data.state === "NotPressedBind") {
                    setFirmwareLoad(<p>Not pressed bind</p>);
                }
                else if (result.data.state === "ModemNotOnline") {
                    setFirmwareLoad(<p>Modem not online</p>);
                }
            }
        });
    }

    return (
        <>
            {rate != null && drone.version < 2000 &&
                <div className='baud_rate'>
                    <p style={{ marginBottom: "0px" }}>Baud rate: </p>
                    <DropdownButton id="dropdown-basic-button" title={rate}>
                        <Dropdown.Item onClick={() => setBaudRate(0)}>9600</Dropdown.Item>
                        <Dropdown.Item onClick={() => setBaudRate(1)}>57600</Dropdown.Item>
                        <Dropdown.Item onClick={() => setBaudRate(2)}>115200</Dropdown.Item>
                    </DropdownButton>
                </div>
            }
            {modemSettings?.rtspUrl != null &&
                <div className='settings-grid ts-16' style={{ alignItems: "baseline" }}>
                    <div className='grid-1-2 t-big'>Основные настройки</div>
                    <div className='grid-3-6'>
                        {!isAstra &&
                            <>
                                <div className='t-big'>RTSP</div>
                                <Form.Control
                                    className='input_settings'
                                    type="text"
                                    value={rtspStr}
                                    onChange={(event) => { setRtspStr(event.target.value) }}
                                />
                                <div className='t-14 t-grey ts-40'>Строка RTSP подключения к ip камере</div>
                            </>
                        }
                        <div className='grid-fpv'>
                            <div className='t-big'>FPV Режим</div>
                            <div className='ogn-switch'>
                                <Switch
                                    onChange={() => setFpvModSettings(!fpvModeSettings)}
                                    checked={fpvModeSettings}
                                    uncheckedIcon={false}
                                    checkedIcon={false}
                                    onColor='#EBFF00'
                                    onHandleColor='#000000'
                                    offHandleColor='#000000'
                                    offColor='#494949'
                                />
                            </div>
                        </div>
                        <div className='t-14 t-grey'>Уменьшает задержку, но может повлиять на качество изображения</div>
                        {fpvModeSettings &&
                            <>
                                <Form.Control
                                    key='fpvLat'
                                    className='input_settings'
                                    type="number"
                                    value={fpvLatencySettings}
                                    onChange={(event) => { setFpvLatencySettings(parseInt(event.target.value)) }}
                                />
                                <div className='t-14 t-grey'>Задержка мс</div>
                            </>
                        }
                    </div>
                    {saveSettingsInfo &&
                        <div className='grid-1-2 save-settings-info'>{saveSettingsInfo}</div>
                    }
                    <div className='grid-3-6'>
                        <Button
                            onClick={saveModemSettings}
                            disabled={!enabledSaveButton}
                            className='t-big'
                        >
                            Сохранить
                        </Button>
                    </div>
                    <div className='grid-max'>
                        <hr />
                    </div>
                </div>
            }
            <div className='settings-grid ts-16' style={{ alignItems: "baseline" }}>
                <div className='grid-1-2 t-big'>Прошивка</div>
                <div className='grid-3-6'>
                    <div className='grid-3-6 t-big'>{t('firmware_version')}: {firmware.normalizeFirmwareVersion(drone.firmwareVersion, drone.version)}</div>
                    {firmwareLoad}
                    {firmwareLoadProgress}
                    {drone.upgradable ?
                        <>
                            <p>Для обновления ПО выполните следующие 4 шага:</p>
                            <div className='upgrade_steps'>
                                <p>1. Скачайте <a href={drone.upgradeUrl}>архив</a> с образом</p>
                                <p>2. Скачайте <a href="https://win32diskimager.org/">Win32 Disk Imager</a></p>
                                <p>3. Запишите образ sdcard.img (файл находится в архиве) на SD карту с помощью Win32 Disk Imager</p>
                                <p>4. Вставьте SD карту в Air-link и дождитесь операции прошивки (индикатор STATUS мерцает белым цветом), после завершения прошивки загорится зеленый или красный индикатор. Если индикатор красный, попробуйте повторить процесс</p>
                            </div>
                        </>
                        :
                        <>
                            {drone.isLastFirmware === 0 ?
                                <>{/*<p>t('latest_firmware_version_is_installed')</p>*/}</>
                                :
                                <>
                                    {firmwareLoadProgress == null &&
                                        <>
                                            {drone.isLastFirmware === 1 ? (
                                                <>
                                                    <p>{t('firmware_version_is_outdated')}</p>
                                                    <Button
                                                        variant="primary"
                                                        style={{ marginBottom: "12px" }}
                                                        onClick={() => { loadLastFirmware() }}
                                                        className='t-big'
                                                    >
                                                        {t('load_latest_version')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <p>{t('firmware_in_process_updating')}</p>
                                            )}
                                        </>
                                    }
                                </>
                            }
                        </>
                    }
                </div>
                <div className='grid-max'>
                    <hr />
                </div>
            </div>

            {(isAstra && subscriptionInfo) &&
                <div className='settings-grid ts-16' style={{ alignItems: "baseline" }}>
                    <div className='grid-1-2 t-big'>Подписка</div>
                    <div className='grid-3-6 t-big'>
                        <div>Действует до {new Date(subscriptionInfo?.timeDeactivation ?? "").toLocaleDateString()}</div>
                    </div>
                    <div className='grid-3-6'>
                        <Button onClick={() => { window.location.href = "/subscriptions" }} className='t-big'>Управление</Button>
                    </div>
                    <div className='grid-max'>
                        <hr />
                    </div>
                </div>
            }

            <div className='settings-grid ts-16' style={{ alignItems: "baseline" }}>
                <div className='grid-1-2'>
                    <Button variant="primary" className='t-big' onClick={unbind}>Отвязать</Button>
                </div>
            </div>

            <Modal size="lg" show={showUnbindModel} onHide={() => { setShowUnbindModel(false) }}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ color: "black" }}>{t('caution')}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ color: "black" }}>
                    <div>{t('are_you_sure_unbind')}</div>
                    {(drone != null && isAstra) &&
                        <>
                            <div>Отвязывать устройство можно не чаще чем раз в месяц</div>
                            {subscriptionInfo?.unbindAvailable ? (
                                <div>Сохраните код для повторного использования: <span className='bold-text'>{subscriptionInfo?.code}</span></div>
                            ) : (
                                <div>Отвязать устройство станет доступно: {subscriptionInfo?.unbindAvailableTime}</div>
                            )}
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className='t-big' onClick={() => { setShowUnbindModel(false) }}>
                        {t('close')}
                    </Button>
                    <Button variant="primary" className='t-big' onClick={confirmUnbind}>
                        {t('unbind')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default MainSettings;